/**
 * TeamTabs: the URL-?tab=-driven tab model and rail for the Team page.
 * Modeled on ClientRecordTabs: a stable tab union, a visibility object, an
 * `isHidden` predicate and a coercion, so a role URL-hacking ?tab=map it
 * cannot see degrades to a tab it can, never a 403 shell. The page owns
 * the tab STATE (searchParams); this module owns the tab MODEL and the
 * rail render.
 *
 * Tab order and names: Team Roster · Availability · Map · SPICE Schedule.
 * The ?tab= values behind them are their own thing (directory / capacity
 * / map / spice_week) -- every deep link, bookmark and redirect keeps
 * working off those, independent of the visible label or its position in
 * the rail.
 *
 * Visibility mirrors the server gates:
 *   - Team Roster (?tab=directory) and SPICE Schedule (?tab=spice_week):
 *     every seat admitted to the page (view_staff_tools) — the SPICE grid
 *     is read-only for a therapist who lacks view_workspace, not hidden;
 *   - Availability (?tab=capacity) and Map (?tab=map): view_workspace, the
 *     gate on the capacity read and the tile relay, so a seat that cannot
 *     read the board never sees a tab whose request would be refused.
 */

import { type ReactNode } from 'react';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';

export type TeamTab = 'directory' | 'spice_week' | 'capacity' | 'map';

export const VALID_TEAM_TABS: ReadonlyArray<TeamTab> = ['directory', 'capacity', 'map', 'spice_week'];

export const TEAM_TAB_LABELS: Record<TeamTab, string> = {
  directory: 'Team Roster',
  capacity: 'Availability',
  map: 'Map',
  spice_week: 'SPICE Schedule',
};

export interface TeamTabVisibility {
  /** Hide Capacity and Map: the caller lacks view_workspace. */
  hideCapacityTabs?: boolean;
}

export function isTeamTabHidden(tab: TeamTab, visibility: TeamTabVisibility): boolean {
  return (tab === 'capacity' || tab === 'map') && !!visibility.hideCapacityTabs;
}

/** Coerce a raw ?tab= value to a tab the caller may see; Directory is the landing. */
export function resolveTeamTab(raw: string | null, visibility: TeamTabVisibility): TeamTab {
  const tab = VALID_TEAM_TABS.includes(raw as TeamTab) ? (raw as TeamTab) : 'directory';
  return isTeamTabHidden(tab, visibility) ? 'directory' : tab;
}

export interface TeamTabsProps extends TeamTabVisibility {
  activeTab: TeamTab;
  onChange: (next: TeamTab) => void;
}

export function TeamTabs({ activeTab, hideCapacityTabs, onChange }: TeamTabsProps): ReactNode {
  const tabs = VALID_TEAM_TABS.filter((t) => !isTeamTabHidden(t, { hideCapacityTabs }));
  return (
    <nav aria-label="Team sections" data-testid="team-tabs">
      <div
        role="tablist"
        className={`flex items-center gap-1 overflow-x-auto ${TAB_RAIL_CLASSES.tablist}`}
      >
        {tabs.map((tab) => {
          const isActive = tab === activeTab;
          return (
            <button
              key={tab}
              id={`team-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="team-tabpanel"
              data-testid={`team-tab-${tab}`}
              onClick={() => onChange(tab)}
              className={
                `${TAB_RAIL_CLASSES.tab} ` +
                (isActive
                  ? `border-forest-700 ${TAB_RAIL_CLASSES.active}`
                  : TAB_RAIL_CLASSES.inactive)
              }
            >
              {TEAM_TAB_LABELS[tab]}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
